<template>
  <v-container>
    <div>
      <template>
        <v-dialog
          v-model="dialog"
          width="876"
          @click:outside="closeModal"
          :retain-focus="false"
        >
          <v-card class="custom-card" rounded="0">
            <v-toolbar color="secondary" class="white--text">
              <v-toolbar-title class="font-weight-bold">
                {{
                  !vaccination.id ? "Add New Vaccination" : "Update Vaccination"
                }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon class="white--text" @click="closeModal">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-form
              ref="form"
              v-model="valid"
              lazy-validation
              @submit.prevent="addVaccinationRecord"
              style="padding: 20px"
            >
              <v-row>
                <v-col cols="12" v-if="vaccination.id">
                  <v-card flat color="transparent">
                    <v-toolbar
                      class="bdr secondary2light"
                      :elevation="0"
                      outlined
                    >
                      <v-toolbar-title
                        class="grey--text text--darken-2"
                        style="font-size: 13px"
                      >
                        Do you really want to edit this record ? Your previous
                        records might get affected.
                      </v-toolbar-title>
                      <v-spacer></v-spacer>
                    </v-toolbar>
                  </v-card>
                </v-col>

                <v-col cols="6">
                  <label class="label">
                    {{ $t("animal.healthRecordInfo.vaccinationType") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>
                        Vaccines are made using several processes. They may
                        contain live viruses that have been attenuated (weakened
                        or altered to not cause illness); inactivated or killed
                        organisms or viruses; inactivated toxins (for bacterial
                        diseases where toxins generated by the bacteria, and not
                        the bacteria themselves, cause illness); or merely
                        segments of the pathogen (this includes both subunit and
                        conjugate vaccines). The different vaccine types each
                        require different development techniques.
                      </span>
                    </v-tooltip>
                  </label>
                  <v-select
                    outlined
                    :placeholder="$t('animal.healthRecordInfo.vaccinationType')"
                    dense
                    v-model="vaccination.vaccinationType"
                    :items="[
                      'Inactivated vaccines',
                      'Live-attenuated vaccines',
                      'Messenger RNA (mRNA) vaccines',
                      'Subunit, recombinant, polysaccharide, and conjugate vaccines',
                      'Toxoid vaccines',
                      'Viral vector vaccines',
                    ]"
                  ></v-select>
                </v-col>
                <v-col cols="6">
                  <label class="label">
                    {{ $t("animal.healthRecordInfo.vaccinationName") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>
                        It is the trade name of vaccine used for administration.
                        e.g. biothrax, vaxchora etc.
                      </span>
                    </v-tooltip>
                  </label>
                  <v-text-field
                    type="text"
                    outlined
                    dense
                    :placeholder="
                      $t('animal.healthRecordInfo.vaccinationNamePlaceholder')
                    "
                    v-model="vaccination.vaccinationName"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <label class="label">
                    {{ $t("animal.healthRecordInfo.vaccinationAge") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span> Date of Manufacture </span>
                    </v-tooltip>
                  </label>
                  <v-menu
                    v-model="vaccinationAgeDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="vaccination.vaccinationAge"
                        placeholder="Select date"
                        append-icon="mdi-calendar"
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        v-on:click:append="vaccinationAgeDateMenu = true"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="vaccination.vaccinationAge"
                      @input="vaccinationAgeDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <label class="label">
                    {{ $t("animal.healthRecordInfo.immunityAfterVaccination") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>
                        {{
                          $t("animal.healthRecordInfo.immunityAfterVaccination")
                        }}
                      </span>
                    </v-tooltip>
                  </label>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        type="number"
                        outlined
                        dense
                        placeholder="Enter value"
                        v-model="vaccination.immunityAfterVaccination"
                        :min="0" 
                        oninput="this.value = Math.abs(this.value)"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-select
                        outlined
                        placeholder="Unit"
                        dense
                        v-model="vaccination.immunityAfterVaccinationUnit"
                        :items="['Day(s)', 'Week(s)', 'Hour(s)']"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <label class="label">
                    {{ $t("animal.healthRecordInfo.immunityLastingPeriod") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>
                        How many days the animal will be immunized after
                        vaccination e.g. 180 days, 200 days, 1 year etc
                      </span>
                    </v-tooltip>
                  </label>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        type="number"
                        outlined
                        dense
                        placeholder="Enter value"
                        v-model="vaccination.immunityLastingPeriod"
                        :min="0" 
                        oninput="this.value = Math.abs(this.value)"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-select
                        outlined
                        placeholder="Select period"
                        dense
                        v-model="vaccination.immunityLastingPeriodUnit"
                        :items="['Day(s)', 'Week(s)', 'Month(s)', 'Year(s)']"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="6">
                  <label class="label">
                    {{ $t("animal.healthRecordInfo.routeOfAdministration") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>
                        The location at which the drug is administered e.g. oral,
                        subcutaneous, intramuscular
                      </span>
                    </v-tooltip>
                  </label>
                  <v-select
                    outlined
                    :placeholder="
                      $t('animal.healthRecordInfo.routeOfAdministration')
                    "
                    dense
                    v-model="vaccination.vaccineRouteOfAdmin"
                    :items="['Oral', 'Subcutaneous', 'Intramuscular']"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <label class="label">
                    {{ $t("animal.healthRecordInfo.vaccinationDosage") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>
                        Its is the amount of vaccine administered to the
                        animal e.g. 0.5ml, 2ml, 5ml etc
                      </span>
                    </v-tooltip>
                  </label>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        type="number"
                        outlined
                        dense
                        placeholder="Enter value"
                        v-model="vaccination.vaccinationDosage"
                        :min="0" 
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-select
                        outlined
                        placeholder="Unit"
                        dense
                        v-model="vaccination.vaccinationDosageUnit"
                        :items="['ml']"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="6">
                  <label class="label">
                    {{ $t("animal.healthRecordInfo.vaccinationDate") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>
                        {{ $t("animal.healthRecordInfo.vaccinationDate") }}
                      </span>
                    </v-tooltip>
                  </label>
                  <v-menu
                    v-model="vaccinationtDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="vaccination.vaccinationDate"
                        placeholder="Select date"
                        append-icon="mdi-calendar"
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        v-on:click:append="vaccinationtDateMenu = true"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="vaccination.vaccinationDate"
                      @input="vaccinationtDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <label class="label">
                    {{ $t("animal.healthRecordInfo.revaccinationTime") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span> Date for booster dose </span>
                    </v-tooltip>
                  </label>
                  <v-menu
                    v-model="revaccinationDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="vaccination.revaccinationDate"
                        placeholder="Select date"
                        append-icon="mdi-calendar"
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        v-on:click:append="revaccinationDateMenu = true"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="vaccination.revaccinationDate"
                      @input="revaccinationDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <label class="label">
                    {{ $t("animal.healthRecordInfo.administratorName") }}
                    <v-tooltip top color="black" max-width="350">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <span>
                        {{ $t("animal.healthRecordInfo.administratorName") }}
                      </span>
                    </v-tooltip>
                  </label>
                  <v-text-field
                    type="text"
                    outlined
                    dense
                    placeholder="Type name"
                    v-model="vaccination.administratorName"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-divider></v-divider>
              <v-card-actions style="padding: 20px">
                <v-spacer></v-spacer>
                <v-btn
                  outlined
                  class="mr-2"
                  right
                  height="44"
                  width="116"
                  depressed
                  color="primary"
                  @click="closeModal"
                >
                  {{ $t("cancel") }}
                </v-btn>
                <v-btn
                  right
                  height="44"
                  width="116"
                  color="primary"
                  type="submit"
                >
                  {{ !vaccination.id ? $t("Submit") : $t("update") }}
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
      </template>
    </div>
  </v-container>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import gql from "graphql-tag";
import moment from "moment";

export default {
  computed: {
    dialog() {
      return this.dialogI;
    },
    routeOfAdministrations() {
      return ["Oral", "Sublingual", "Intragastric gavage", "Rectal"];
    },
  },
  data() {
    return {
      revaccinationDateMenu: false,
      vaccination: {},
      vaccinationAgeDateMenu: false,
      vaccinationtDateMenu: false,
      valid: true,
    };
  },
  methods: {
    closeModal() {
      this.$emit("closeDialog");
    },
    addVaccinationRecord() {
      if (!this.$refs.form.validate()) return;
      if(Object.keys(this.vaccination).length == 0){
            this.$notify({
                    //title: `No details entered.`,
                    text: 'No details entered.',
                    type: "error",
                    position: "top right",
                });
            return;
          }
          
      this.startLoading();
      const mutation =
        this.vaccination && this.vaccination.id
          ? `mutation updateVaccination ($data: UpdateVaccinationInput!) {
            updateVaccination (updateVaccinationInput: $data) {
              id
            }
          }`
          : `mutation ($data: CreateVaccinationInput!) {
            createVaccination (createVaccinationInput: $data) {
              id
            }
          }`;

          

      const data = {
        animalId: parseInt(this.$route.params.animal_number),
        administrator_name: this.vaccination.administratorName,
        vaccination_age: this.vaccination.vaccinationAge
          ? moment(this.vaccination.vaccinationAge).format("YYYY-MM-DD")
          : null,
        vaccination_date: this.vaccination.vaccinationDate
          ? moment(this.vaccination.vaccinationDate).format("YYYY-MM-DD")
          : null,
        vaccination_dosage: this.vaccination.vaccinationDosage,
        vaccination_dosage_unit: this.vaccination.vaccinationDosageUnit,
        vaccination_name: this.vaccination.vaccinationName,
        vaccination_type: this.vaccination.vaccinationType,
        vaccine_route_of_admin: this.vaccination.vaccineRouteOfAdmin,
        revaccination_time: this.vaccination.revaccinationDate
          ? moment(this.vaccination.revaccinationDate).format("YYYY-MM-DD")
          : null,
        immunity_after_vaccination: this.vaccination.immunityAfterVaccination,
        immunity_after_vaccination_unit:
          this.vaccination.immunityAfterVaccinationUnit,
        immunity_lasting_period: this.vaccination.immunityLastingPeriod,
        immunity_lasting_period_unit:
          this.vaccination.immunityLastingPeriodUnit,
      };

      if (this.vaccination && this.vaccination.id) {
        data.id = parseInt(this.vaccination.id);
      }
      this.$apollo
        .mutate({
          mutation: gql`
            ${mutation}
          `,
          variables: {
            data,
          },
        })
        .then((res) => {
          let msg = "added";

          if (this.vaccination.id) {
            msg = "updated";
          }
          if (res.data) {
            this.$notify({
              title: `Vaccination record ${msg} successfully`,
              text: res.message,
              type: "success",
              position: "top right",
            });

            this.$emit("onUpdate");
          } else {
            this.$notify({
              title: `Vaccination Record Not ${msg}`,
              text: res.message,
              type: "error",
              position: "top right",
            });
          }
          this.stopLoading();
          this.closeModal();
        })
        .catch((err) => {
          console.log(err.data);
          this.stopLoading();
        });
    },
  },
  mounted() {
    if (this.vaccinationItem) {
      this.vaccination = {
        ...this.vaccinationItem,
        revaccinationtDate: this.vaccinationItem.vaccinationDate
          ? moment(this.vaccinationItem.revaccinationtDate).format("YYYY-MM-DD")
          : null,
        vaccinationDate: this.vaccinationItem.vaccinationDate
          ? moment(this.vaccinationItem.vaccinationDate).format("YYYY-MM-DD")
          : null,
        vaccinationAge: this.vaccinationItem.vaccinationAge
          ? moment(this.vaccinationItem.vaccinationAge).format("YYYY-MM-DD")
          : null,
      };
    }
  },
  props: ["dialogI", "vaccinationItem"],
  mixins: [loadingMixin],
};
</script>

<style scoped>
::v-deep .v-messages__message {
    line-height: 12px;
    word-break: break-word;
    /* overflow-wrap: break-word; */
    word-wrap: break-word;
    -webkit-hyphens: auto;
    hyphens: auto;
    white-space: nowrap;
}
</style>
